import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, HostBinding, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { from, interval } from 'rxjs';
import { concatMap, finalize, take, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { RolesService } from '../../services/roles/roles.service';
import { FileTypes } from '../../entities/file/file';
import { FilesService } from '../../services/file/file.service';
import { File as FileEntity } from '../../entities/file/file';
import { Page } from '../../entities/page/page';
import { RefetchCoordinator } from '../../services/refetch-coordinator/refetch-coordinator.service';
import { Reloadable } from '../../services/command/command.service';

@Component({
  selector: 'app-file-list',
  templateUrl: './file-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileListComponent implements OnInit, OnDestroy, Reloadable {

  private readonly destroyRef = inject(DestroyRef);
  readonly rolesService = inject(RolesService);
  private readonly filesService = inject(FilesService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);
  private unregisterRefetch?: () => void;

  readonly headline = input<string>();
  readonly ownerId = input<string>();
  readonly ownerType = input<string>();
  readonly role = input<string>();
  readonly emptyMessage = input<string>();
  readonly createButtonLabel = input<string>();
  readonly maxFileSize = input<number>(10 * 1024 * 1024); // 10MB default

  readonly files = signal<FileEntity[]>([]);
  readonly totalElements = signal<number>(0);
  readonly currentPage = signal<number>(0);
  readonly pageSize = 10;
  readonly uploading = signal(false);
  readonly uploadProgress = signal<{ current: number; total: number } | null>(null);
  readonly menuVisible = signal(false);
  readonly isDragging = signal(false);
  readonly dropError = signal<string | null>(null);

  readonly totalPages = computed(() => Math.ceil(this.totalElements() / this.pageSize));
  readonly hasNextPage = computed(() => this.currentPage() < this.totalPages() - 1);
  readonly hasPreviousPage = computed(() => this.currentPage() > 0);
  readonly showPagination = computed(() => this.totalElements() > this.pageSize);

  // Message dictionary for i18n readiness
  private readonly messages = {
    dropHere: 'Dateien hier ablegen',
    upload: 'Dateien hochladen',
    uploaded: 'hochgeladen',
    fileSizeExceeded: 'Datei(en) überschreiten {size} Limit',
    invalidFormat: 'Ungültiges Dateiformat',
    rejected: 'Dateien abgelehnt (ungültiges Format oder zu groß)',
    skipped: '{count} Datei(en) übersprungen',
    skippedFormat: '(ungültiges Format)',
    skippedSize: '(überschreitet {size})',
    skippedBoth: '(ungültiges Format oder zu groß)'
  } as const;

  // Expose messages for template
  readonly dropHereText = this.messages.dropHere;
  readonly uploadedText = this.messages.uploaded;
  readonly uploadText = this.messages.upload;

  private readonly defaultAcceptedTypes: readonly FileTypes[] = [
    FileTypes.PDF,
    FileTypes.JPG,
    FileTypes.JPEG,
    FileTypes.PNG
  ] as const;

  readonly acceptedFileTypes = input<FileTypes[]>(this.defaultAcceptedTypes as FileTypes[]);

  readonly acceptString = computed(() => this.acceptedFileTypes().join(', '));

  constructor() {
    effect(() => {
      const id = this.ownerId();
      if (id) {
        this.loadFiles();
      }
    });
  }

  ngOnInit(): void {
    const ownerId = this.ownerId();
    const ownerType = this.ownerType();
    if (ownerId && ownerType) {
      this.unregisterRefetch = this.refetchCoordinator.register(
        this,
        ownerType,
        ownerId
      );
    }
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
  }

  reload(): void {
    this.loadFiles();
  }

  loadFiles(): void {
    this.filesService.getFilesPagedWithPagination({
      ownerId: this.ownerId(),
      ownerType: this.ownerType(),
      role: this.role(),
      pageNumber: this.currentPage(),
      pageSize: this.pageSize
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      this.files.set(result.content || []);
      this.totalElements.set(result.totalElements || 0);
    });
  }

  onFileDeleted(): void {
    // If we're on a page beyond the first and only one file is left,
    // go back to the previous page before reloading
    if (this.currentPage() > 0 && this.files().length === 1) {
      this.currentPage.update(page => page - 1);
    }
    this.loadFiles();
    this.emitRefetchEvent();
  }

  fileInputChangedEvent(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (this.uploading() || !input.files?.length || !this.ownerId()) {
      return;
    }

    const allFiles = Array.from(input.files);
    input.value = '';  // Reset input to allow re-uploading the same file

    const { valid: validFiles, invalidType, invalidSize } = this.filterValidFiles(allFiles);

    if (validFiles.length === 0) {
      this.showValidationError(invalidType, invalidSize);
      return;
    }

    if (validFiles.length < allFiles.length) {
      this.showSkippedFilesWarning(invalidType, invalidSize);
    }

    this.uploadFiles(validFiles);
  }

  // Drag & Drop handlers
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.uploading() && this.rolesService.hasRole('framework_files_manage')) {
      this.isDragging.set(true);
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    if (this.uploading() || !this.ownerId()) {
      return;
    }

    if (!this.rolesService.hasRole('framework_files_manage')) {
      return;
    }

    const files = event.dataTransfer?.files;
    if (files?.length) {
      const allFiles = Array.from(files);
      const { valid: validFiles, invalidType, invalidSize } = this.filterValidFiles(allFiles);

      if (validFiles.length === 0) {
        this.showValidationError(invalidType, invalidSize);
      } else if (validFiles.length < allFiles.length) {
        this.showSkippedFilesWarning(invalidType, invalidSize);
        this.uploadFiles(validFiles);
      } else {
        this.uploadFiles(validFiles);
      }
    }
  }

  private showValidationError(invalidType: number, invalidSize: number): void {
    if (invalidSize > 0 && invalidType === 0) {
      this.showDropError(this.messages.fileSizeExceeded.replace('{size}', this.formatFileSize(this.maxFileSize())));
    } else if (invalidType > 0 && invalidSize === 0) {
      this.showDropError(this.messages.invalidFormat);
    } else {
      this.showDropError(this.messages.rejected);
    }
  }

  private showSkippedFilesWarning(invalidType: number, invalidSize: number): void {
    const skipped = invalidType + invalidSize;
    let message = this.messages.skipped.replace('{count}', String(skipped));
    if (invalidType > 0 && invalidSize > 0) {
      message += ' ' + this.messages.skippedBoth;
    } else if (invalidSize > 0) {
      message += ' ' + this.messages.skippedSize.replace('{size}', this.formatFileSize(this.maxFileSize()));
    } else {
      message += ' ' + this.messages.skippedFormat;
    }
    this.showDropError(message);
  }

  private showDropError(message: string): void {
    this.dropError.set(message);
    setTimeout(() => this.dropError.set(null), 3000);
  }

  private filterValidFiles(files: globalThis.File[]): { valid: globalThis.File[]; invalidType: number; invalidSize: number } {
    const acceptedMimeTypes = this.acceptedFileTypes().map(t => t.toLowerCase());
    const maxSize = this.maxFileSize();

    let invalidType = 0;
    let invalidSize = 0;

    const valid = files.filter(file => {
      const mimeType = file.type.toLowerCase();
      if (!acceptedMimeTypes.includes(mimeType)) {
        invalidType++;
        return false;
      }
      if (maxSize && file.size > maxSize) {
        invalidSize++;
        return false;
      }
      return true;
    });

    return { valid, invalidType, invalidSize };
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  private uploadFiles(files: globalThis.File[]): void {
    if (!files.length) return;

    const batchId = uuidv4();

    this.uploading.set(true);
    this.uploadProgress.set({ current: 0, total: files.length });

    let uploadedCount = 0;

    this.filesService.uploadFilesV2Batch(
      files,
      this.ownerId()!,
      this.role()!,
      batchId,
      this.ownerType()
    ).pipe(
      tap(() => {
        uploadedCount++;
        this.uploadProgress.set({ current: uploadedCount, total: files.length });
      }),
      finalize(() => this.returnUpload()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private returnUpload(): void {
    setTimeout(() => {
      this.uploading.set(false);
      this.uploadProgress.set(null);
      this.currentPage.set(0); // Reset to first page after upload
      this.loadFiles();
      this.emitRefetchEvent();
    }, 100);
  }

  nextPage(): void {
    if (this.hasNextPage()) {
      this.currentPage.update(page => page + 1);
      this.loadFiles();
    }
  }

  previousPage(): void {
    if (this.hasPreviousPage()) {
      this.currentPage.update(page => page - 1);
      this.loadFiles();
    }
  }

  private emitRefetchEvent(): void {
    const ownerId = this.ownerId();
    const ownerType = this.ownerType();
    if (ownerId && ownerType) {
      this.refetchCoordinator.emit({
        entityType: ownerType,
        entityId: ownerId
      });
    }
  }
}
